/**
 * Client-side proctoring violation tracker
 * Monitors ONLY:
 * 1. Candidate opened new tab/window
 * 2. Candidate speaking/audio detected
 * 
 * Also triggers video recording when violations occur
 */
import API from './api';

class ProctoringViolationTracker {
  constructor(candidateId, assessmentStartTime) {
    this.candidateId = candidateId;
    this.assessmentStartTime = assessmentStartTime;
    this.violations = [];
    this.mediaStream = null;
    this.audioContext = null;
    this.analysisRunning = false;
    this.onViolationCallback = null; // Callback to trigger video recording
    this.setupListeners();
  }

  // Set callback to trigger video recording on violation
  setViolationCallback(callback) {
    this.onViolationCallback = callback;
  }

  async setupListeners() {
    // 1. DETECT NEW TAB OPENED - when user switches away from assessment
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.logViolation('tab_switch', 'Candidate left assessment and opened a new tab');
      }
    });

    // 2. DETECT SPEECH - audio detection will be initialized when camera stream is available
  }

  // Call this from AssessmentPage with the existing camera stream
  initializeAudioDetectionWithStream(cameraStream) {
    if (!cameraStream || this.analysisRunning) return;
    
    try {
      this.mediaStream = cameraStream;
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const source = this.audioContext.createMediaStreamSource(cameraStream);
      const analyser = this.audioContext.createAnalyser();
      analyser.fftSize = 256; // Smaller FFT for faster detection
      analyser.smoothingTimeConstant = 0.85; // More responsive
      source.connect(analyser);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      let lastSpeechTime = 0;
      const SPEECH_THRESHOLD = 30; // Lower threshold (more sensitive)
      const SPEECH_DEBOUNCE = 3000; // 3 seconds between detections

      const checkAudio = () => {
        if (!this.analysisRunning) return;
        
        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        
        // Check if average frequency is above threshold (indicates speech/sound)
        if (average > SPEECH_THRESHOLD) {
          const now = Date.now();
          // Only log if enough time has passed since last detection
          if (now - lastSpeechTime > SPEECH_DEBOUNCE) {
            this.logViolation('speech_detected', 'Audio/speaking detected during assessment');
            lastSpeechTime = now;
          }
        }

        requestAnimationFrame(checkAudio);
      };

      this.analysisRunning = true;
      checkAudio();
    } catch (e) {
      console.warn('Audio detection setup failed:', e.message);
    }
  }

  logViolation(type, description) {
    // Count violations of this specific type
    const typeCount = this.violations.filter(v => v.type === type).length;
    
    // Stop accepting violations of this type after reaching 5
    if (typeCount >= 5) {
      return;
    }

    const violation = {
      type,
      description,
      timestamp: new Date().toISOString(),
      detectedAt: new Date()
    };
    
    this.violations.push(violation);
    
    // Trigger video recording when violation occurs
    if (this.onViolationCallback) {
      console.log('[Proctoring] Violation detected, triggering video recording:', type);
      this.onViolationCallback(type, violation);
    }
    
    // Report violations immediately when detected (1 or more)
    // Stop accepting more than 5 per type to prevent spam
    const tabSwitchCount = this.violations.filter(v => v.type === 'tab_switch').length;
    const speechCount = this.violations.filter(v => v.type === 'speech_detected').length;
    
    if (tabSwitchCount <= 5 && speechCount <= 5) {
      this.reportViolations();
    }
  }

  async reportViolations() {
    try {
      const response = await API.post('/candidates/report-violations', {
        candidateId: this.candidateId,
        violations: this.violations
      });
    } catch (e) {
      console.error('Failed to report violations:', e);
    }
  }

  getViolationCount() {
    return this.violations.length;
  }

  getViolations() {
    return this.violations;
  }

  cleanup() {
    this.analysisRunning = false;
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
    }
  }
}

export default ProctoringViolationTracker;
